import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "../../../../containers/app/layout";
import { AddNewEmployeeWrapper } from "./styled";
import { H2, P, Label, Span } from "../../../../components/typography/styled";
import { BaseFieldSet } from "../../../../components/form/fieldset/styled";
import { BaseInput } from "../../../../components/form/input/styled";
import { AddNewEmployeeRow } from "./styled";
import { BaseButton } from "../../../../components/button/styled";
import { Column } from "../../../../components/flex/styled";
import { BaseSelect } from "../../../../components/form/select/styled";
import { BaseTextArea } from "../../../../components/form/textarea/styled";
import { AddEmployeeSuccessModal } from "../../../../containers/app/modals/addemployeesuccessmodal";
import { addNewEmployeeService } from "../../../../utils/apis/employee/addNewEmployee";
import { DotLoader } from "react-spinners";
import Cookies from "universal-cookie";
import { formatDateToDDMMYYYY } from "../../../../config/app/dateFormatter";
import { getDepartments } from "../../../../utils/apis/department/getDepartments";
import { retrieveAllBanks } from "../../../../utils/external/fetchAllBanks";
import { SuccessModal } from "../../../../containers/app/modals/successmodal";

export const AddNewEmployee = () => {
  const cookies = new Cookies();
  const COMPANY_ID = cookies.get("COMPANY_ID");
  const TOKEN = cookies.getAll().TOKEN;
  const REACT_APP_PAYSTACK_SK = process.env.REACT_APP_PAYSTACK_SK;

  const navigate = useNavigate();
  const initialFormDetails = useMemo(
    () => ({
      personalInfo: {
        firstName: "",
        surname: "",
        othername: "",
        address: "",
        phone: "",
        email: "",
        dateOfBirth: "",
      },
      jobInfo: {
        jobPosition: "",
        dateHired: "",
        departmentName: null,
      },
      payrollSetup: {
        annualGrossPay: "",
        salaryBankName: "",
        salaryBankAccount: "",
        pensionFirmName: "",
        pensionAccount: "",
        pensionId: "",
        taxNumber: "",
        optInForPension: true,
      },
      nextofKinInfo: {
        title: "",
        fullName: "",
        relationship: "",
        phone: "",
        address: "",
      },
      emergencyContactInfo: {
        title: "",
        fullName: "",
        relationship: "",
        phone: "",
        address: "",
      },
    }),
    [],
  );

  const [step, setStep] = useState(1);
  const [matches, setMatches] = useState(false);
  const [isFormReset, setIsFormReset] = useState(false);
  const [error, setError] = useState(null);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [banks, setBanks] = useState([]);
  const [formDetails, setFormDetails] = useState(initialFormDetails);

  const handleChange = (e, section) => {
    const { name, value, type, checked } = e.target;
    setFormDetails((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [name]: type === "checkbox" ? checked : value,
      },
    }));
  };

  const handleCloseSuccessModal = () => {
    setIsSuccessModalOpen(false);
    return navigate("/employees");
  };

  const handleAddAnotherEmployee = () => {
    setIsSuccessModalOpen(false);
    resetForm();
  };

  const handlePersistModal = () => {
    return setIsSuccessModalOpen(true);
  };

  const handleClickPrevious = () => {
    setStep((prev) => {
      return prev - 1;
    });
  };

  const handleClickNext = async (e, step) => {
    e.preventDefault();
    const form = document.querySelector("form");
    if (form && !form.reportValidity()) {
      return;
    }
    if (step === 4) {
      return await handleSubmit(e);
    }
    setStep((prev) => {
      return prev + 1;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    const formattedFormDetails = {
      ...formDetails,
      jobInfo: {
        ...formDetails.jobInfo,
        dateHired: formatDateToDDMMYYYY(formDetails.jobInfo.dateHired),
      },
      personalInfo: {
        ...formDetails.personalInfo,
        dateOfBirth: formatDateToDDMMYYYY(formDetails.personalInfo.dateOfBirth),
      },
    };
    try {
      const response = await addNewEmployeeService(
        TOKEN,
        formattedFormDetails,
        COMPANY_ID,
      );
      if (response.status) {
        setIsLoading(false);
        setIsSuccessModalOpen(true);
      } else {
        setIsLoading(false);
        setError(
          "Addition of new employee failed. Please check your credentials and try again.",
        );
        console.error(
          "Addition of new employee failed. Please check your credentials and try again.",
        );
      }
    } catch (error) {
      setIsLoading(false);
      setError(`Addition of new employee failed. ${error.message}`);
      console.error("Addition of new employee failed:", error);
    }
  };

  const resetForm = useCallback(() => {
    setStep(1);
    setFormDetails(initialFormDetails);
  }, [initialFormDetails]);

  useEffect(() => {
    if (isFormReset) {
      resetForm();
      setIsFormReset(false);
    }
  }, [isFormReset, resetForm]);

  useEffect(() => {
    const handleResize = () => {
      setMatches(window.screen.availWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await getDepartments(TOKEN, COMPANY_ID);
        return setDepartments(response.data);
      } catch (error) {
        console.error(error);
      }
    };
    fetchDepartments();
  }, [TOKEN, COMPANY_ID]);

  useEffect(() => {
    const fetchAllBanks = async () => {
      try {
        const response = await retrieveAllBanks(REACT_APP_PAYSTACK_SK);
        return setBanks(response);
      } catch (error) {
        console.error(error);
      }
    };
    fetchAllBanks();
  }, [REACT_APP_PAYSTACK_SK]);

  return (
    <Layout id={"employees"} title={"Add new employee"}>
      <AddNewEmployeeWrapper>
        <SuccessModal
          open={isSuccessModalOpen}
          handleClickOutside={handlePersistModal}
          className={"edit-employee-success-modal"}
          title={"Success"}
          message={"Employee has been successfully edited"}
          callToAction={"Close"}
          handleCallToActionClick={handleCloseSuccessModal}
          secondaryAction={"Add another Employee"}
          handleSecondaryActionClick={handleAddAnotherEmployee}
        />
        <Column className="employeeForm">
          {step === 1 && (
            <div className="formText">
              <H2>Personal Details</H2>
              <P>Add user by capturing all the details</P>
            </div>
          )}
          {step === 2 && (
            <div className="formText">
              <H2>Corporate Details</H2>
              <P>Add corporate information</P>
            </div>
          )}
          {step === 3 && (
            <div className="formText">
              <H2>Payroll Setup</H2>
              <P>Set up payroll details</P>
            </div>
          )}
          {step === 4 && (
            <div className="formText">
              <H2>Personal Info</H2>
              <P>Add contact information</P>
            </div>
          )}
          <form onSubmit={handleSubmit}>
            {step === 1 && (
              <Fragment>
                <BaseFieldSet>
                  <Label>Surname</Label>
                  <BaseInput
                    type="text"
                    name="surname"
                    placeholder="Doe"
                    value={formDetails.personalInfo.surname}
                    onChange={(e) => handleChange(e, "personalInfo")}
                    required
                  />
                </BaseFieldSet>
                <AddNewEmployeeRow>
                  <BaseFieldSet>
                    <Label>First Name</Label>
                    <BaseInput
                      type="text"
                      name="firstName"
                      placeholder="John"
                      value={formDetails.personalInfo.firstName}
                      onChange={(e) => handleChange(e, "personalInfo")}
                      required
                    />
                  </BaseFieldSet>
                  <BaseFieldSet>
                    <Label> Other Name</Label>
                    <BaseInput
                      type="text"
                      name="othername"
                      placeholder="Peter"
                      value={formDetails.personalInfo.othername}
                      onChange={(e) => handleChange(e, "personalInfo")}
                      required
                    />
                  </BaseFieldSet>
                </AddNewEmployeeRow>
                <AddNewEmployeeRow>
                  <BaseFieldSet>
                    <Label>Address</Label>
                    <BaseInput
                      type="text"
                      name="address"
                      placeholder="eg. No 1, Awolowo Road, Ikoyi, Lagos"
                      value={formDetails.personalInfo.address}
                      onChange={(e) => handleChange(e, "personalInfo")}
                      required
                    />
                  </BaseFieldSet>
                  <BaseFieldSet>
                    <Label>Date Of Birth</Label>
                    <BaseInput
                      type="date"
                      name="dateOfBirth"
                      value={formDetails.personalInfo.dateOfBirth}
                      onChange={(e) => handleChange(e, "personalInfo")}
                      required
                    />
                  </BaseFieldSet>
                </AddNewEmployeeRow>
                <AddNewEmployeeRow>
                  <BaseFieldSet>
                    <Label>Email</Label>
                    <BaseInput
                      type="email"
                      name="email"
                      placeholder="john.doe@example.com"
                      value={formDetails.personalInfo.email}
                      onChange={(e) => handleChange(e, "personalInfo")}
                      required
                    />
                  </BaseFieldSet>
                  <BaseFieldSet>
                    <Label>Phone Number</Label>
                    <BaseInput
                      type="tel"
                      name="phone"
                      placeholder="08012345678"
                      value={formDetails.personalInfo.phone}
                      onChange={(e) => handleChange(e, "personalInfo")}
                      required
                    />
                  </BaseFieldSet>
                </AddNewEmployeeRow>
                <BaseButton
                  backgroundcolor={"var(--color-primary)"}
                  width={"fit-content"}
                  onClick={(e) => handleClickNext(e, step)}
                  style={{
                    marginLeft: "auto",
                    padding: "0.75rem 1.5rem",
                  }}
                >
                  Next
                </BaseButton>
              </Fragment>
            )}
            {step === 2 && (
              <Fragment>
                <AddNewEmployeeRow>
                  <BaseFieldSet>
                    <Label>Department Name</Label>
                    <BaseSelect
                      required
                      name="departmentName"
                      value={formDetails.jobInfo.departmentName}
                      onChange={(e) => handleChange(e, "jobInfo")}
                    >
                      <option value={null}>Select Department</option>
                      {departments.map((department, index) => {
                        return (
                          <option key={index} value={department.name}>
                            {department.name}
                          </option>
                        );
                      })}
                    </BaseSelect>
                  </BaseFieldSet>
                  <BaseFieldSet>
                    <Label>Job Position</Label>
                    <BaseInput
                      type="text"
                      name="jobPosition"
                      placeholder="e.g Software Engineer"
                      value={formDetails.jobInfo.jobPosition}
                      onChange={(e) => handleChange(e, "jobInfo")}
                      required
                    />
                  </BaseFieldSet>
                </AddNewEmployeeRow>
                <BaseFieldSet>
                  <Label>Date Hired</Label>
                  <BaseInput
                    type="date"
                    name="dateHired"
                    placeholder="Enter Date Hired"
                    value={formDetails.jobInfo.dateHired}
                    onChange={(e) => handleChange(e, "jobInfo")}
                    required
                  />
                </BaseFieldSet>
                <Column className="submit-column">
                  <BaseButton
                    backgroundcolor={"var(--color-primary)"}
                    width={matches ? "-webkit-fill-available" : "fit-content"}
                    onClick={(e) => handleClickPrevious(e, step)}
                    style={{
                      padding: "0.75rem 1.5rem",
                    }}
                  >
                    Previous
                  </BaseButton>
                  <BaseButton
                    backgroundcolor={"var(--color-primary)"}
                    width={matches ? "-webkit-fill-available" : "fit-content"}
                    onClick={(e) => handleClickNext(e, step)}
                    style={{
                      marginLeft: "auto",
                      padding: "0.75rem 1.5rem",
                    }}
                  >
                    Next
                  </BaseButton>
                </Column>
              </Fragment>
            )}
            {step === 3 && (
              <Fragment>
                <Column className="switch-column">
                  <Label>Opt In For Pension</Label>
                  <BaseFieldSet style={{ flex: "unset" }}>
                    <Label className="switch">
                      <BaseInput
                        type="checkbox"
                        className="pension-check"
                        name="optInForPension"
                        onChange={(e) => handleChange(e, "payrollSetup")}
                        checked={formDetails.payrollSetup.optInForPension}
                      />
                      <Span className="slider round"></Span>
                    </Label>
                  </BaseFieldSet>
                </Column>
                <AddNewEmployeeRow>
                  <BaseFieldSet>
                    <Label>Annual Gross Pay</Label>
                    <BaseInput
                      type="text"
                      name="annualGrossPay"
                      value={formDetails.payrollSetup.annualGrossPay}
                      onChange={(e) => handleChange(e, "payrollSetup")}
                      required
                    />
                  </BaseFieldSet>
                  <BaseFieldSet>
                    <Label>Salary Bank Name</Label>
                    <BaseSelect
                      required
                      name="salaryBankName"
                      value={formDetails.payrollSetup.salaryBankName}
                      onChange={(e) => handleChange(e, "payrollSetup")}
                    >
                      <option value="">Select Bank</option>
                      {banks?.map((bank, index) => (
                        <option key={index} value={bank.name}>
                          {bank.name}
                        </option>
                      ))}
                    </BaseSelect>
                  </BaseFieldSet>
                </AddNewEmployeeRow>
                <AddNewEmployeeRow>
                  <BaseFieldSet>
                    <Label>Salary Bank Account</Label>
                    <BaseInput
                      type="text"
                      name="salaryBankAccount"
                      value={formDetails.payrollSetup.salaryBankAccount}
                      onChange={(e) => handleChange(e, "payrollSetup")}
                      required
                    />
                  </BaseFieldSet>
                  <BaseFieldSet>
                    <Label>Tax Identification Number</Label>
                    <BaseInput
                      type="text"
                      name="taxNumber"
                      value={formDetails.payrollSetup.taxNumber}
                      onChange={(e) => handleChange(e, "payrollSetup")}
                      required
                    />
                  </BaseFieldSet>
                </AddNewEmployeeRow>
                <AddNewEmployeeRow>
                  <BaseFieldSet>
                    <Label>Pension Firm Name</Label>
                    <BaseSelect
                      required={formDetails.payrollSetup.optInForPension}
                      name="pensionFirmName"
                      value={formDetails.payrollSetup.pensionFirmName}
                      onChange={(e) => handleChange(e, "payrollSetup")}
                      disabled={!formDetails.payrollSetup.optInForPension}
                    >
                      <option value="">Select Bank</option>
                      {banks?.map((bank, index) => (
                        <option key={index} value={bank.name}>
                          {bank.name}
                        </option>
                      ))}
                    </BaseSelect>
                  </BaseFieldSet>
                  <BaseFieldSet>
                    <Label>Pension Account</Label>
                    <BaseInput
                      type="text"
                      name="pensionAccount"
                      value={formDetails.payrollSetup.pensionAccount}
                      onChange={(e) => handleChange(e, "payrollSetup")}
                      required={formDetails.payrollSetup.optInForPension}
                      disabled={!formDetails.payrollSetup.optInForPension}
                    />
                  </BaseFieldSet>
                  <BaseFieldSet>
                    <Label>Pension ID</Label>
                    <BaseInput
                      type="text"
                      name="pensionId"
                      value={formDetails.payrollSetup.pensionId}
                      onChange={(e) => handleChange(e, "payrollSetup")}
                      required={formDetails.payrollSetup.optInForPension}
                      disabled={!formDetails.payrollSetup.optInForPension}
                    />
                  </BaseFieldSet>
                </AddNewEmployeeRow>
                <Column className="submit-column">
                  <BaseButton
                    backgroundcolor={"var(--color-primary)"}
                    width={matches ? "-webkit-fill-available" : "fit-content"}
                    onClick={(e) => handleClickPrevious(e, step)}
                    style={{
                      padding: "0.75rem 1.5rem",
                    }}
                  >
                    Previous
                  </BaseButton>
                  <BaseButton
                    backgroundcolor={"var(--color-primary)"}
                    width={matches ? "-webkit-fill-available" : "fit-content"}
                    onClick={(e) => handleClickNext(e, step)}
                    style={{
                      marginLeft: "auto",
                      padding: "0.75rem 1.5rem",
                    }}
                  >
                    Next
                  </BaseButton>
                </Column>
              </Fragment>
            )}
            {step === 4 && (
              <Fragment>
                <AddNewEmployeeRow>
                  <BaseFieldSet>
                    <Label>Next of Kin’s Title</Label>
                    <BaseSelect
                      required
                      name="title"
                      value={formDetails.nextofKinInfo.title}
                      onChange={(e) => handleChange(e, "nextofKinInfo")}
                    >
                      <option value="" hidden></option>
                      <option value="Mr">Mr</option>
                      <option value="Mrs">Mrs</option>
                      <option value="Miss">Miss</option>
                      <option value="Other">Other</option>
                    </BaseSelect>
                  </BaseFieldSet>
                  <BaseFieldSet>
                    <Label>Next of Kin’s Full Name</Label>
                    <BaseInput
                      type="text"
                      name="fullName"
                      value={formDetails.nextofKinInfo.fullName}
                      onChange={(e) => handleChange(e, "nextofKinInfo")}
                      required
                    />
                  </BaseFieldSet>
                </AddNewEmployeeRow>
                <AddNewEmployeeRow>
                  <BaseFieldSet>
                    <Label>Relationship</Label>
                    <BaseSelect
                      name="relationship"
                      value={formDetails.nextofKinInfo.relationship}
                      onChange={(e) => handleChange(e, "nextofKinInfo")}
                    >
                      <option value="" hidden></option>
                      <option value="Spouse">Spouse</option>
                      <option value="Child">Child</option>
                      <option value="Parent">Parent</option>
                      <option value="Sibling">Sibling</option>
                      <option value="Other">Other</option>
                    </BaseSelect>
                  </BaseFieldSet>
                  <BaseFieldSet>
                    <Label>Phone Number</Label>
                    <BaseInput
                      type="tel"
                      name="phone"
                      value={formDetails.nextofKinInfo.phone}
                      onChange={(e) => handleChange(e, "nextofKinInfo")}
                      required
                    />
                  </BaseFieldSet>
                </AddNewEmployeeRow>
                <BaseFieldSet>
                  <Label>Contact Address</Label>
                  <BaseTextArea
                    className="address"
                    type="text"
                    name="address"
                    value={formDetails.nextofKinInfo.address}
                    onChange={(e) => handleChange(e, "nextofKinInfo")}
                    required
                  />
                </BaseFieldSet>
                <H2>Emergency Contacts</H2>
                <AddNewEmployeeRow>
                  <BaseFieldSet>
                    <Label>Contact’s Title</Label>
                    <BaseSelect
                      required
                      name="title"
                      value={formDetails.emergencyContactInfo.title}
                      onChange={(e) => handleChange(e, "emergencyContactInfo")}
                    >
                      <option value="" hidden></option>
                      <option value="Mr">Mr</option>
                      <option value="Mrs">Mrs</option>
                      <option value="Miss">Miss</option>
                      <option value="Other">Other</option>
                    </BaseSelect>
                  </BaseFieldSet>
                  <BaseFieldSet>
                    <Label>Contact’s Full Name</Label>
                    <BaseInput
                      type="text"
                      name="fullName"
                      value={formDetails.emergencyContactInfo.fullName}
                      onChange={(e) => handleChange(e, "emergencyContactInfo")}
                      required
                    />
                  </BaseFieldSet>
                </AddNewEmployeeRow>
                <AddNewEmployeeRow>
                  <BaseFieldSet>
                    <Label>Relationship</Label>
                    <BaseSelect
                      required
                      name="relationship"
                      value={formDetails.emergencyContactInfo.relationship}
                      onChange={(e) => handleChange(e, "emergencyContactInfo")}
                    >
                      <option value="" hidden></option>
                      <option value="Father">Father</option>
                      <option value="Mother">Mother</option>
                      <option value="Other">Other</option>
                    </BaseSelect>
                  </BaseFieldSet>
                  <BaseFieldSet>
                    <Label>Phone Number</Label>
                    <BaseInput
                      type="tel"
                      name="phone"
                      value={formDetails.emergencyContactInfo.phone}
                      onChange={(e) => handleChange(e, "emergencyContactInfo")}
                      required
                    />
                  </BaseFieldSet>
                </AddNewEmployeeRow>
                <BaseFieldSet>
                  <Label>Contact Address</Label>
                  <BaseTextArea
                    className="address"
                    type="text"
                    name="address"
                    value={formDetails.emergencyContactInfo.address}
                    onChange={(e) => handleChange(e, "emergencyContactInfo")}
                    required
                  />
                </BaseFieldSet>
                <Column className="submit-column">
                  <BaseButton
                    backgroundcolor={"var(--color-primary)"}
                    width={matches ? "-webkit-fill-available" : "fit-content"}
                    onClick={(e) => handleClickPrevious(e, step)}
                    style={{
                      padding: "0.75rem 1.5rem",
                    }}
                  >
                    Previous
                  </BaseButton>
                  <BaseButton
                    type="submit"
                    backgroundcolor={"var(--color-primary)"}
                    width={matches ? "-webkit-fill-available" : "fit-content"}
                    style={{
                      marginLeft: "auto",
                      padding: "0.75rem 1.5rem",
                    }}
                  >
                    {isLoading ? (
                      <DotLoader
                        size={20}
                        color="white"
                        className="dotLoader"
                      />
                    ) : (
                      <Span>Submit</Span>
                    )}
                  </BaseButton>
                </Column>
              </Fragment>
            )}
            {error && <P style={{ color: "red" }}>{error}</P>}
          </form>
        </Column>
        <AddEmployeeSuccessModal
          width={"40%"}
          setIsFormReset={setIsFormReset}
          surname={" " + formDetails.personalInfo.surname}
          firstName={formDetails.personalInfo.firstName}
        />
      </AddNewEmployeeWrapper>
    </Layout>
  );
};
